import React, { useState, useEffect } from "react";
import { db, DEFAULT_STORE_ID } from "../../../firebase/firebase";
import {
  getLocalizedCategories,
  getActiveStoreCategories,
} from "../../../firebase/category-functions";
import { useLanguage } from "../../../context/LanguageContext";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import "./CollectionSlider.css";

const CollectionSlider = () => {
  const [collections, setCollections] = useState([]);
  const [index, setIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Translation object for the collection title
  const translations = {
    en: {
      title: "Explore the Collection",
      loading: "Loading collections...",
    },
    ar: {
      title: "استكشف المجموعة",
      loading: "جاري تحميل المجموعات...",
    },
  };

  const t = translations[language] || translations.en;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const storeId = DEFAULT_STORE_ID;

        // Use the centralized function to get active categories
        const fetchedCategories = await getActiveStoreCategories(storeId, {
          sortBy: "createdAt",
          sortDirection: "desc",
        });

        if (fetchedCategories.length > 0) {
          // Apply localization to the categories
          const localizedCategories = getLocalizedCategories(
            fetchedCategories,
            language
          );
          // Filter categories that have featured images for the collection slider
          const categoriesWithImages = localizedCategories.filter(
            (category) => category.featuredImage
          );
          setCollections(categoriesWithImages);
        }
      } catch (err) {
        console.error("Error fetching categories:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [language]);

  useEffect(() => {
    const interval = setInterval(() => next(), 5000);
    return () => clearInterval(interval);
  }, [index, collections]);

  const prev = () => {
    setIndex((prevIndex) =>
      prevIndex === 0 ? collections.length - 1 : prevIndex - 1
    );
  };

  const next = () => {
    setIndex((prevIndex) =>
      prevIndex === collections.length - 1 ? 0 : prevIndex + 1
    );
  };

  const variants = {
    left: {
      x: isMobile ? "-110%" : "-210%",
      scale: 1,
      opacity: 0.3,
      filter: "blur(1px)",
    },
    center: { x: "0%", scale: 1, opacity: 1, filter: "blur(0px)" },
    right: {
      x: isMobile ? "110%" : "210%",
      scale: 1,
      opacity: 0.3,
      filter: "blur(1px)",
    },
  };

  const initialVariants = {
    left: { x: isMobile ? "-150%" : "-300%", opacity: 0 },
    center: { opacity: 0 },
    right: { x: isMobile ? "150%" : "300%", opacity: 0 },
  };

  const exitVariants = initialVariants;

  const getPosition = (i) => {
    const total = collections.length;
    const left = (index - 1 + total) % total;
    const right = (index + 1) % total;

    if (i === left) return "left";
    if (i === index) return "center";
    if (i === right) return "right";
    return "hidden";
  };

  if (loading) {
    return <div className="collection-section">{t.loading}</div>;
  }

  if (collections.length === 0) {
    return null; // Don't show the slider if no collections with images
  }

  return (
    <div className="collection-section">
      <h2 className="collection-title">{t.title}</h2>

      <div className="collection-slider">
        {!isMobile && (
          <button className="arrow left" onClick={prev}>
            ←
          </button>
        )}

        <div className="slider-window">
          <AnimatePresence mode="popLayout" initial={false}>
            {collections.map((item, i) => {
              const position = getPosition(i);
              if (position === "hidden") return null;

              return (
                <motion.div
                  key={item.id}
                  className="slide"
                  drag="x"
                  dragConstraints={{ left: 0, right: 0 }}
                  onDragEnd={(event, info) => {
                    if (info.offset.x > 50) prev();
                    else if (info.offset.x < -50) next();
                  }}
                  initial={initialVariants[position]}
                  animate={variants[position]}
                  exit={exitVariants[position]}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  style={{
                    cursor: position === "center" ? "pointer" : "default",
                  }}
                >
                  <img
                    src={item.featuredImage}
                    alt={item.name}
                    className="slider-img"
                    style={{ cursor: "pointer" }}
                    aria-label={`Go to ${item.name} collection`}
                    tabIndex={0}
                    onClick={() => {
                      navigate(
                        `/shop?category=${encodeURIComponent(
                          item.originalName ||
                            item.multilingual?.en?.name ||
                            item.name
                        )}`
                      );
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        navigate(
                          `/shop?category=${encodeURIComponent(
                            item.originalName ||
                              item.multilingual?.en?.name ||
                              item.name
                          )}`
                        );
                      }
                    }}
                  />

                  <AnimatePresence mode="wait">
                    {position === "center" && (
                      <motion.p
                        key="title"
                        className="image-title"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        transition={{ duration: 0.5 }}
                      >
                        {item.name}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {!isMobile && (
          <button className="arrow right" onClick={next}>
            →
          </button>
        )}
      </div>
    </div>
  );
};

export default CollectionSlider;
